import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { trackFormSubmit, trackCTAClick } from '../utils/analytics';
import { submitContactForm } from '../utils/submitForm';

interface FormData {
  name: string;
  email: string;
  company: string;
  phone: string;
  message: string;
  consent: boolean;
}

type Errors = Partial<Record<keyof FormData, string>>;

interface ContactFormProps {
  variant?: 'light' | 'dark';
}

export default function ContactForm({ variant = 'light' }: ContactFormProps) {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    consent: false,
  });
  const [errors, setErrors] = useState<Errors>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = (): Errors => {
    const next: Errors = {};
    if (!formData.name.trim()) {
      next.name = language === 'de' ? 'Bitte geben Sie Ihren Namen ein.' : 'Please enter your name.';
    }
    if (!formData.email.trim()) {
      next.email = language === 'de' ? 'Bitte geben Sie Ihre E-Mail-Adresse ein.' : 'Please enter your email.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      next.email = language === 'de' ? 'Bitte geben Sie eine gültige E-Mail-Adresse ein.' : 'Please enter a valid email.';
    }
    if (!formData.message.trim()) {
      next.message = language === 'de' ? 'Bitte beschreiben Sie Ihr Anliegen.' : 'Please describe your request.';
    }
    if (!formData.consent) {
      next.consent = language === 'de' ? 'Bitte stimmen Sie der Verarbeitung Ihrer Daten zu.' : 'Please consent to data processing.';
    }
    return next;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    setSubmitError(false);
    trackFormSubmit('contact_form');
    trackCTAClick('contact_form_submit', 'contact_form');

    const ok = await submitContactForm({
      ...formData,
      consent: formData.consent ? 'yes' : 'no',
    });
    setSubmitting(false);

    if (ok) {
      setSubmitted(true);
      setFormData({ name: '', email: '', company: '', phone: '', message: '', consent: false });
    } else {
      setSubmitError(true);
    }
  };

  const isDark = variant === 'dark';

  const labelClass = `block text-sm font-semibold mb-1.5 ${isDark ? 'text-white' : 'text-dark dark:text-white'}`;

  const inputClass = (hasError?: boolean) =>
    `w-full px-5 py-3 rounded-full border-2 focus:outline-none transition-colors ${
      isDark
        ? 'bg-white border-white text-dark placeholder:text-gray-400 focus:border-accent'
        : 'bg-white border-line text-dark placeholder:text-gray-400 focus:border-primary'
    } ${hasError ? 'border-red-400 focus:border-red-500' : ''}`;

  if (submitted) {
    return (
      <div className="text-center py-10">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 ${isDark ? 'bg-white/20' : 'bg-green-100'}`}>
          <CheckCircle2 className={`w-8 h-8 ${isDark ? 'text-white' : 'text-green-600'}`} aria-hidden="true" />
        </div>
        <p className={`text-lg font-bold mb-3 ${isDark ? 'text-white' : 'text-dark'}`}>
          {t('contact.form.success')}
        </p>
        <button
          onClick={() => setSubmitted(false)}
          className={`text-sm font-semibold ${isDark ? 'text-accent-light' : 'text-primary'} hover:underline`}
        >
          {language === 'de' ? 'Weitere Anfrage senden' : 'Send another request'}
        </button>
      </div>
    );
  }

  const consentText = language === 'de' ? (
    <>
      Ich habe die <Link to="/datenschutz" className={`font-semibold ${isDark ? 'text-accent-light' : 'text-primary'}`}>Datenschutzerklärung</Link> gelesen und stimme der Verarbeitung meiner Angaben zur Bearbeitung meiner Anfrage zu.
    </>
  ) : (
    <>
      I have read the <Link to="/en/datenschutz" className={`font-semibold ${isDark ? 'text-accent-light' : 'text-primary'}`}>privacy policy</Link> and consent to the processing of my data for handling my request.
    </>
  );

  const fieldIds = {
    name: isDark ? 'cf-name-dark' : 'cf-name',
    email: isDark ? 'cf-email-dark' : 'cf-email',
    company: isDark ? 'cf-company-dark' : 'cf-company',
    phone: isDark ? 'cf-phone-dark' : 'cf-phone',
    message: isDark ? 'cf-message-dark' : 'cf-message',
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor={fieldIds.name} className={labelClass}>
          {language === 'de' ? 'Name' : 'Name'} <span className="text-red-400" aria-hidden="true">*</span>
        </label>
        <input
          type="text"
          id={fieldIds.name}
          name="name"
          required
          aria-required="true"
          value={formData.name}
          onChange={handleChange}
          aria-invalid={!!errors.name}
          className={inputClass(!!errors.name)}
          placeholder={t('contact.form.placeholder.name')}
        />
        {errors.name && (
          <p role="alert" className="mt-1.5 text-sm text-red-400 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" /> {errors.name}
          </p>
        )}
      </div>

      <div>
        <label htmlFor={fieldIds.email} className={labelClass}>
          {language === 'de' ? 'E-Mail' : 'Email'} <span className="text-red-400" aria-hidden="true">*</span>
        </label>
        <input
          type="email"
          id={fieldIds.email}
          name="email"
          required
          aria-required="true"
          value={formData.email}
          onChange={handleChange}
          aria-invalid={!!errors.email}
          className={inputClass(!!errors.email)}
          placeholder={t('contact.form.placeholder.email')}
        />
        {errors.email && (
          <p role="alert" className="mt-1.5 text-sm text-red-400 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" /> {errors.email}
          </p>
        )}
      </div>

      <div>
        <label htmlFor={fieldIds.company} className={labelClass}>
          {language === 'de' ? 'Firma' : 'Company'}
        </label>
        <input
          type="text"
          id={fieldIds.company}
          name="company"
          value={formData.company}
          onChange={handleChange}
          className={inputClass(false)}
          placeholder={t('contact.form.placeholder.company')}
        />
      </div>

      <div>
        <label htmlFor={fieldIds.phone} className={labelClass}>
          {language === 'de' ? 'Telefon' : 'Phone'} <span className={`font-normal ${isDark ? 'text-blue-200' : 'text-gray-400'}`}>({language === 'de' ? 'optional' : 'optional'})</span>
        </label>
        <input
          type="tel"
          id={fieldIds.phone}
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className={inputClass(false)}
          placeholder={t('contact.form.placeholder.phone')}
        />
      </div>

      <div>
        <label htmlFor={fieldIds.message} className={labelClass}>
          {language === 'de' ? 'Anliegen' : 'Message'} <span className="text-red-400" aria-hidden="true">*</span>
        </label>
        <textarea
          id={fieldIds.message}
          name="message"
          required
          aria-required="true"
          value={formData.message}
          onChange={handleChange}
          rows={4}
          aria-invalid={!!errors.message}
          className={`${inputClass(!!errors.message)} rounded-2xl resize-none`}
          placeholder={t('contact.form.placeholder.message')}
        />
        {errors.message && (
          <p role="alert" className="mt-1.5 text-sm text-red-400 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" /> {errors.message}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="consent" className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            id="consent"
            name="consent"
            checked={formData.consent}
            onChange={handleChange}
            aria-invalid={!!errors.consent}
            className="mt-1 w-5 h-5 rounded border-2 border-gray-300 text-primary focus:ring-primary flex-shrink-0"
          />
          <span className={`text-sm leading-relaxed ${isDark ? 'text-blue-50' : 'text-muted'}`}>
            {consentText}
          </span>
        </label>
        {errors.consent && (
          <p role="alert" className="mt-1.5 text-sm text-red-400 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" /> {errors.consent}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full px-8 py-4 rounded-full font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed bg-dark text-white hover:bg-gray-800"
      >
        {submitting
          ? (language === 'de' ? 'Wird gesendet …' : 'Sending …')
          : (language === 'de' ? 'Jetzt anfragen' : 'Request now')}
        {!submitting && <Send className="w-5 h-5" />}
      </button>

      {submitError && (
        <p role="alert" className="text-sm text-red-400 flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          {language === 'de'
            ? 'Beim Senden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder rufen Sie uns an.'
            : 'An error occurred. Please try again or call us.'}
        </p>
      )}
    </form>
  );
}
